// src/components/common/TeamPageHeader.js
import React from 'react';
import { Shield } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';

export const getTeamInitials = (name) => {
  if (!name) return 'T';
  const words = name.split(' ');
  if (words.length === 1) {
    return name.substring(0, 2).toUpperCase();
  }
  return words.map(word => word[0]).join('').substring(0, 2).toUpperCase();
};

const TeamPageHeader = ({
  title,
  logoUrl,
  teamName,
  teamColors,
  teamMotto,
  bannerConfig,
  isCommissioner = false,
  subtitle = null,
  badges = null,
  actions = null,
}) => {
  return (
    <div
      className={`${dynastyTheme.components.card.highlighted} p-4 flex items-center`}
      style={{ minHeight: bannerConfig ? `${bannerConfig.height}px` : '120px' }}
    >
      <div className="flex items-center justify-between gap-4 w-full">
        {/* Left side: Logo and Team Info */}
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0">
            {logoUrl ? (
              <div className="relative group">
                <img
                  src={logoUrl}
                  alt={`${teamName} Logo`}
                  className="w-24 h-24 rounded-xl object-cover border-2 border-neutral-700 shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'flex';
                  }}
                />
                <div
                  className="w-24 h-24 rounded-xl border-2 border-neutral-700 shadow-lg hidden items-center justify-center text-2xl font-bold"
                  style={{
                    background: teamColors ?
                      `linear-gradient(135deg, ${teamColors.primary || '#FFD700'}, ${teamColors.secondary || '#8B7500'})` :
                      'linear-gradient(135deg, #FFD700, #8B7500)',
                    color: teamColors?.text || '#000000'
                  }}
                >
                  {getTeamInitials(teamName)}
                </div>
                {/* Hover effect */}
                <div className="absolute inset-0 rounded-xl bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                  <Shield className="w-6 h-6 text-yellow-400" />
                </div>
              </div>
            ) : (
              <div
                className="w-24 h-24 rounded-xl border-2 border-neutral-700 shadow-lg flex items-center justify-center text-2xl font-bold"
                style={{
                  background: teamColors ?
                    `linear-gradient(135deg, ${teamColors.primary || '#FFD700'}, ${teamColors.secondary || '#8B7500'})` :
                    'linear-gradient(135deg, #FFD700, #8B7500)',
                  color: teamColors?.text || '#000000'
                }}
              >
                {getTeamInitials(teamName)}
              </div>
            )}
          </div>

          <div className="flex-1">
            <h1 className={`${bannerConfig?.titleSize || 'text-3xl'} ${bannerConfig?.titleStyle || 'font-bold'} ${dynastyTheme.classes.text.white} mb-1`}>
              {title}
            </h1>
            {teamMotto && (
              <p className={`text-sm italic ${dynastyTheme.classes.text.neutralLight} mb-2`}>
                "{teamMotto}"
              </p>
            )}
            {subtitle && (
              <div className="flex items-center gap-4 text-sm">
                {subtitle}
              </div>
            )}
          </div>
        </div>

        {/* Right side: Badges + Actions */}
        <div className="flex-shrink-0 flex flex-col items-end gap-3">
          <div className="flex items-center space-x-2">
            {badges !== null ? badges : (
              <>
                <span className={`${dynastyTheme.components.badge.success} backdrop-blur-md bg-neutral-900/60`}>
                  ACTIVE
                </span>
                {isCommissioner && (
                  <span className={`px-3 py-1 rounded text-sm font-semibold ${dynastyTheme.classes.bg.primary} ${dynastyTheme.classes.text.black} shadow-xl`}>
                    COMMISSIONER
                  </span>
                )}
              </>
            )}
          </div>
          {actions && (
            <div className="flex items-center gap-2">
              {actions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamPageHeader;
